import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';

const ParticleBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;

    let DPR = Math.max(1, window.devicePixelRatio || 1);
    let animationFrameId: number;

    function resize() {
      if (!canvas || !ctx) return;
      DPR = Math.max(1, window.devicePixelRatio || 1);
      canvas.width = Math.floor(window.innerWidth * DPR);
      canvas.height = Math.floor(window.innerHeight * DPR);
      canvas.style.width = window.innerWidth + 'px';
      canvas.style.height = window.innerHeight + 'px';
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
      setupParticles();
    }

    // Particle config
    let particles: any[] = [];
    let mouse = { x: null as number | null, y: null as number | null, active: false };
    const config = {
      particleColor: '255,255,255', // White particles for contrast
      bgColor: '0,0,0',
      maxParticles: 150,
      baseSpeed: 0.2,
      linkDistance: 120,
      particleRadius: { min: 0.8, max: 2.5 },
      mouseRadius: 200, // Radius of mouse influence
      mouseForce: 0.03, // Strength of mouse attraction/repulsion
    };

    function rand(a: number, b: number) {
      return a + Math.random() * (b - a);
    }

    function setupParticles() {
      particles = [];
      const area = window.innerWidth * window.innerHeight;
      const target = Math.min(config.maxParticles, Math.max(30, Math.floor(area / 12000)));
      for (let i = 0; i < target; i++) {
        particles.push({
          x: Math.random() * window.innerWidth,
          y: Math.random() * window.innerHeight,
          vx: rand(-config.baseSpeed, config.baseSpeed),
          vy: rand(-config.baseSpeed, config.baseSpeed),
          r: rand(config.particleRadius.min, config.particleRadius.max),
        });
      }
    }

    const handleMouseMove = (e: MouseEvent) => {
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      mouse.x = e.clientX - rect.left;
      mouse.y = e.clientY - rect.top;
      mouse.active = true;
    };

    const handleMouseLeave = () => {
      mouse.x = null;
      mouse.y = null;
      mouse.active = false;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!canvas) return;
      const t = e.touches[0];
      if (!t) return;
      const rect = canvas.getBoundingClientRect();
      mouse.x = t.clientX - rect.left;
      mouse.y = t.clientY - rect.top;
      mouse.active = true;
    };

    const handleTouchEnd = () => {
      mouse.x = null;
      mouse.y = null;
      mouse.active = false;
    };

    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseleave', handleMouseLeave);
    canvas.addEventListener('touchmove', handleTouchMove, { passive: true });
    canvas.addEventListener('touchend', handleTouchEnd);

    function updateParticles(delta: number) {
      for (let p of particles) {
        // Mouse interaction
        if (mouse.active && mouse.x != null && mouse.y != null) {
          const dx = mouse.x - p.x;
          const dy = mouse.y - p.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          if (distance < config.mouseRadius) {
            const force = (1 - distance / config.mouseRadius) * config.mouseForce * delta;
            const angle = Math.atan2(dy, dx);
            // Attract particles to mouse
            p.vx += Math.cos(angle) * force;
            p.vy += Math.sin(angle) * force;
            
            // Add some damping to prevent runaway velocities
            p.vx *= 0.98;
            p.vy *= 0.98;
          } else {
            // Gradually return to base speed when not near mouse
            p.vx *= 0.99;
            p.vy *= 0.99;
            if (Math.abs(p.vx) < config.baseSpeed) {
              p.vx += (Math.random() - 0.5) * config.baseSpeed * 0.1;
            }
            if (Math.abs(p.vy) < config.baseSpeed) {
              p.vy += (Math.random() - 0.5) * config.baseSpeed * 0.1;
            }
          }
        }
        
        p.x += p.vx * delta;
        p.y += p.vy * delta;

        if (p.x < -10) p.x = window.innerWidth + 10;
        if (p.x > window.innerWidth + 10) p.x = -10;
        if (p.y < -10) p.y = window.innerHeight + 10;
        if (p.y > window.innerHeight + 10) p.y = -10;
      }
    }

    function draw() {
      if (!ctx || !canvas) return;
      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Draw particles with glow effect
      for (let p of particles) {
        // Calculate opacity based on mouse distance
        let opacity = 0.3;
        if (mouse.active && mouse.x != null && mouse.y != null) {
          const dx = mouse.x - p.x;
          const dy = mouse.y - p.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          if (distance < config.mouseRadius) {
            opacity = 0.3 + (1 - distance / config.mouseRadius) * 0.7;
          }
        }
        
        ctx.fillStyle = `rgba(${config.particleColor},${opacity})`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }

      // Include mouse as an attractor node
      let nodes = particles;
      if (mouse.active && mouse.x != null) {
        nodes = particles.concat([{ x: mouse.x, y: mouse.y, r: 0 }]);
      }

      // Draw links
      const thresh = config.linkDistance;
      const threshSq = thresh * thresh;
      ctx.lineWidth = 1;
      for (let i = 0; i < nodes.length; i++) {
        const a = nodes[i];
        for (let j = i + 1; j < nodes.length; j++) {
          const b = nodes[j];
          let dx = a.x - b.x;
          let dy = a.y - b.y;
          let d2 = dx * dx + dy * dy;
          if (d2 <= threshSq) {
            const alpha = Math.max(0, (thresh - Math.sqrt(d2)) / thresh) * 0.5;
            ctx.strokeStyle = `rgba(${config.particleColor},${alpha.toFixed(3)})`;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }
    }

    // Animation loop
    let last = performance.now();
    function loop(now: number) {
      const dt = Math.min(40, now - last);
      last = now;
      updateParticles(dt * 0.06); // Scale down velocity
      draw();
      animationFrameId = requestAnimationFrame(loop);
    }

    // Initialize
    window.addEventListener('resize', resize, { passive: true });
    resize();
    animationFrameId = requestAnimationFrame(loop);

    // Cleanup
    return () => {
      window.removeEventListener('resize', resize);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseleave', handleMouseLeave);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', handleTouchEnd);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <Box
      component="canvas"
      ref={canvasRef}
      id="bg"
      sx={{
        position: 'fixed',
        inset: 0,
        display: 'block',
        zIndex: 0,
        pointerEvents: 'all',
      }}
    />
  );
};

export default ParticleBackground;